import React from 'react';
import { useQuery } from 'hooks/apollo';
import { FormattedMessage } from 'react-intl';
import { gql } from '@apollo/client';

import { EDIT_GLOBAL_SEGMENTS, EDIT_SHARED_SEGMENT, SUPER_USER } from 'shared/constants/permissions';

import {
  Form,
  Input,
  Checkbox,
  Select,
  Row,
  Col,
  Alert,
  Collapse,
  Tooltip,
  Space,
  Button,
} from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import globalMessages from 'translations/messages';
import LicenseTag from 'components/LicenseTag';
import NoAccessTag from 'components/NoAccessTag';
import useUser from 'hooks/useUser';
import MatomoIcon from 'assets/images/matomo-icon.png';
import useDataSource from 'hooks/useDataSource';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import useCustomer from 'hooks/useCustomer';
import SegmentFiltersForm, { toFilterOptionsInput } from '../SegmentFiltersForm';
import messages from '../messages';

const { Option } = Select;
const { Panel } = Collapse;

const GET_WEBSITES = gql`
  query SegmentForm_getWebsites($customerId: ID!){
    websites(customerId: $customerId) {
      id
      title
      matomoId
      matomoSegmentDimensions {
        id
        name
        index
      }
      matomoCustomDimensionScopes {
        id
        numSlotsLeft
      }
    }
  }
`;

const MatomoWebsiteSelect = ({ websites, selectedWebsiteId, ...props }) => {
  const { hasWebsiteAccess } = useUser();
  const selectedWebsite = websites.find(w => w.id === selectedWebsiteId);
  const customDimensions = selectedWebsite?.matomoSegmentDimensions.map(d => ({
    id: d.id,
    index: d.index,
    name: `${d.index} - ${d.name}`,
  }));

  const numSlotsLeft = selectedWebsite?.matomoCustomDimensionScopes.find(s => s.id === 'visit')?.numSlotsLeft;

  if (numSlotsLeft > 0) {
    customDimensions.push({ index: 'new', name: 'New slot' });
  }

  return (
    <Row gutter={16}>
      <Col span={12}>
        <Form.Item name="syncToWebsite" label={<FormattedMessage {...messages.syncToWebsite} />} rules={[{ required: true }]}>
          <Select {...props}>
            {websites.map(w => {
              const hasAccess = hasWebsiteAccess(w.id);
              return (
                <Option key={w.id} value={w.id} disabled={!hasAccess}>
                  {w.title}
                  {!hasAccess && <NoAccessTag style={{ marginLeft: '.5em' }} />}
                </Option>
              );
            })}
          </Select>
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item name="matomoCustomDimension" label={<FormattedMessage {...messages.matomoCustomDimension} />} rules={[{ required: true }]}>
          <Select {...props}>
            {customDimensions?.map(c => <Option key={c.id} value={c.index}>{c.name}</Option>)}
          </Select>
        </Form.Item>
      </Col>
    </Row>
  );
};

const SegmentForm = ({
  form,
  customerId,
  sharedSegmentCount,
  matomoSegmentCount,
  surveyGroupId,
  displayedSurveyId,
  dateRange,
  filterSegment,
  onSubmit,
  isEditable,
  isNewSegment,
  onFilterLoaded,
}) => {
  const { filterOptions = [] } = filterSegment;
  const { hasCustomerPermission } = useCustomer();
  const { hasDataSourcePermission, getDataSourceLicenseSetting } = useDataSource();
  const { data } = useQuery(GET_WEBSITES, {
    variables: {
      customerId,
    },
  });

  const handleSubmit = ({ friendlyTitle, syncToMatomo, syncToWebsite, matomoCustomDimension, shared, filterOptionsOperator, filterOptionsInverted, ...selectedFilters }) => {
    onSubmit({
      id: filterSegment.id,
      friendlyTitle,
      syncToMatomo,
      syncToWebsite: syncToMatomo ? syncToWebsite : null,
      matomoCustomDimension: matomoCustomDimension === 'new' ? null : matomoCustomDimension,
      shared,
      filterOptionsOperator,
      filterOptionsInverted,
      filterOptions: toFilterOptionsInput(filterOptions, selectedFilters),
    });
  };

  const matomoWebsites = data?.websites.filter(w => w.matomoId);

  const initialValues = {
    friendlyTitle: filterSegment.friendlyTitle,
    shared: filterSegment.shared,
    syncToMatomo: filterSegment.syncToMatomo,
    syncToWebsite: filterSegment.syncToWebsite?.id,
    matomoCustomDimension: filterSegment.matomoCustomDimension,
    filterOptionsOperator: filterSegment.filterOptionsOperator || 'AND',
    filterOptionsInverted: filterSegment.filterOptionsInverted,
  };

  const sharedSegmentLimit = getDataSourceLicenseSetting('sharedSegmentLimit');
  const reachedSegmentLimit = !filterSegment.shared && sharedSegmentLimit != null && sharedSegmentCount >= sharedSegmentLimit;
  const canShareSegment = hasDataSourcePermission(EDIT_SHARED_SEGMENT) && !reachedSegmentLimit;
  const matomoSegmentLimit = getDataSourceLicenseSetting('matomoSegmentLimit');
  const reachedMatomoLimit = !filterSegment.syncToMatomo && matomoSegmentLimit != null && matomoSegmentCount >= matomoSegmentLimit;
  const canShareToMatomo = canShareSegment && !reachedMatomoLimit;
  const isGlobalSegment = !!filterSegment.segment;
  const canEditGlobalSegment = hasCustomerPermission(EDIT_GLOBAL_SEGMENTS);

  let sharedTooltip;
  if (filterSegment.shared) {
    if (!isEditable) {
      sharedTooltip = <FormattedMessage {...messages.sharedByTooltip} values={filterSegment} />;
    } else {
      sharedTooltip = <FormattedMessage {...messages.sharedSegmentTooltip} />;
    }
  }

  const onValuesChange = (changedValues) => {
    if (changedValues.syncToWebsite) {
      const website = matomoWebsites.find(w => w.id === changedValues.syncToWebsite);
      if (website) {
        form.setFieldsValue({ matomoCustomDimension: null });
      }
    }
  };

  return (
    <Form
      form={form}
      className="segment-form"
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={initialValues}
      onValuesChange={onValuesChange}
      scrollToFirstError={{
        behavior: (actions) => actions.forEach(({ el, top }) => {
          if (el.classList.contains('ant-drawer-body')) {
            el.scrollTop = 0;
          } else {
            el.scrollTop = top;
          }
        }),
      }}
    >
      {filterSegment.isMergedSegment && <Alert message={<FormattedMessage {...messages.mergedSegment} />} type="info" showIcon style={{ marginBottom: '1em' }} />}

      <Collapse defaultActiveKey={isNewSegment || isGlobalSegment ? ['settings'] : []} style={{ marginTop: -24 }}>
        <Panel
          key="settings"
          forceRender
          header={<FormattedMessage {...globalMessages.settings} />}
          extra={(
            <Space>
              {sharedTooltip && (
              <Tooltip title={sharedTooltip} placement="top">
                <TeamOutlined />
              </Tooltip>
              )}
              {filterSegment.syncToMatomo && (
              <Tooltip title={<FormattedMessage {...messages.syncToMatomoTooltip} />} placement="top">
                <img className="matomo-icon" src={MatomoIcon} alt="Matomo" />
              </Tooltip>
              )}
            </Space>
          )}
        >

          <Form.Item name="friendlyTitle" label={<FormattedMessage {...messages.segmentName} />} rules={[{ required: true, message: <FormattedMessage {...globalMessages.required} /> }]}>
            <Input disabled={!isEditable} />
          </Form.Item>

          <Form.Item name="shared" valuePropName="checked" extra={reachedSegmentLimit && <LicenseTag setting="sharedSegmentLimit" showTooltip />}>
            <Checkbox disabled={!isEditable || (!canShareSegment && !hasDataSourcePermission(SUPER_USER))}><FormattedMessage {...messages.sharedSegment} /></Checkbox>
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prevValues, curValues) => prevValues.shared !== curValues.shared}>
            {({ getFieldValue }) => (getFieldValue('shared') && matomoWebsites?.length > 0 ? (
              <>
                <Form.Item name="syncToMatomo" valuePropName="checked" extra={reachedMatomoLimit && <LicenseTag setting="matomoSegmentLimit" showTooltip />}>
                  <Checkbox disabled={!isEditable || (!canShareToMatomo && !hasDataSourcePermission(SUPER_USER))}><FormattedMessage {...messages.syncToMatomo} /></Checkbox>
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prevValues, curValues) => prevValues.syncToMatomo !== curValues.syncToMatomo || prevValues.syncToWebsite !== curValues.syncToWebsite}>
                  {() => (getFieldValue('syncToMatomo') ? (
                    <MatomoWebsiteSelect disabled={!isEditable || (!canShareToMatomo && !hasDataSourcePermission(SUPER_USER))} websites={matomoWebsites} selectedWebsiteId={getFieldValue('syncToWebsite')} />
                  ) : null)}
                </Form.Item>
              </>
            ) : null)}
          </Form.Item>

          {!isGlobalSegment && (
            <>
              <Form.Item name="filterOptionsOperator" label={<FormattedMessage {...messages.filterOptionsOperator} />}>
                <Select disabled={!isEditable}>
                  <Option value="AND"><FormattedMessage {...messages.filterOptionsOperatorAnd} /></Option>
                  <Option value="OR"><FormattedMessage {...messages.filterOptionsOperatorOr} /></Option>
                </Select>
              </Form.Item>

              <Form.Item name="filterOptionsInverted" valuePropName="checked">
                <Checkbox disabled={!isEditable}><FormattedMessage {...messages.filterOptionsInverted} /></Checkbox>
              </Form.Item>
            </>
          )}
        </Panel>
      </Collapse>

      {isGlobalSegment ? (
        canEditGlobalSegment && (
          <>
            <Alert message={<FormattedMessage {...messages.globalSegmentInfo} values={{ globalSegmentName: <b>{filterSegment.segment.title}</b> }} />} type="info" showIcon style={{ marginTop: '1em', marginBottom: '1em' }} />
            <Link to={`/tools/segments/${filterSegment.segment.id}`}><Button type="primary" block>Edit global segment</Button></Link>
          </>
        )
      ) : (
        <SegmentFiltersForm
          filterOptions={filterOptions}
          surveyGroupId={surveyGroupId}
          displayedSurveyId={displayedSurveyId}
          customerId={customerId}
          dateRange={dateRange}
          isEditable={isEditable}
          onFilterLoaded={onFilterLoaded}
        />
      )}
    </Form>
  );
};

export default SegmentForm;
